import { Check, ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag, SectionHeading } from "@/components/landing/Reveal";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/lib/i18n";

export default function Pricing({ packages }) {
  const { t, dyn } = useLang();
  return (
    <section id="paket" data-testid="pricing-section" className="bg-ink py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTag number="06" label={t("pricing.tag")} />
        <div className="grid gap-10 lg:grid-cols-2 lg:items-end">
          <SectionHeading>{t("pricing.heading")}</SectionHeading>
          <Reveal delay={0.15}>
            <p className="max-w-md text-base leading-relaxed text-fog lg:ml-auto">
              {t("pricing.sub")}
            </p>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-6 lg:grid-cols-3">
          {packages.map((pkg, i) => (
            <Reveal key={`${pkg.name}-${i}`} delay={0.08 + i * 0.12} className="h-full">
              <article
                data-testid={`package-card-${pkg.name.toLowerCase()}`}
                className={`relative flex h-full flex-col rounded-2xl p-8 transition-transform duration-500 sm:p-10 ${
                  pkg.recommended
                    ? "border border-mint/40 bg-forest text-bone shadow-[0_30px_80px_-30px_rgba(22,78,61,0.8)] lg:-translate-y-3"
                    : "border border-white/10 bg-surface text-bone"
                }`}
              >
                {pkg.badge && (
                  <span
                    data-testid={`package-badge-${pkg.name.toLowerCase()}`}
                    className="absolute -top-3.5 left-8 rounded-full bg-mint px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink"
                  >
                    {dyn(pkg.badge)}
                  </span>
                )}
                <h3 className="font-mono text-sm uppercase tracking-[0.3em]">{pkg.name}</h3>
                <p
                  data-testid={`package-price-${pkg.name.toLowerCase()}`}
                  className="mt-6 font-display text-4xl font-black tracking-tight sm:text-5xl"
                >
                  {pkg.price}
                </p>
                {pkg.payLink && (
                  <a
                    data-testid={`package-paylink-${pkg.name.toLowerCase()}`}
                    href={pkg.payLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mint transition-colors hover:text-bone"
                  >
                    {t("pricing.pay")} <ArrowUpRight className="h-3 w-3" />
                  </a>
                )}
                <p className={`mt-3 text-sm leading-relaxed ${pkg.recommended ? "text-bone/80" : "text-fog"}`}>
                  {dyn(pkg.subtitle)}
                </p>
                <ul className="mt-8 flex-1 space-y-3.5">
                  {pkg.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${pkg.recommended ? "text-mint" : "text-moss"}`} />
                      <span className={pkg.recommended ? "text-bone/90" : "text-bone/80"}>{dyn(feature)}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`package-cta-${pkg.name.toLowerCase()}`}
                  onClick={() => scrollToId("#boka")}
                  className={`group mt-10 flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-colors duration-300 ${
                    pkg.recommended
                      ? "bg-mint text-ink hover:bg-bone"
                      : "border border-white/20 text-bone hover:border-mint hover:text-mint"
                  }`}
                >
                  {t("nav.bokaLong")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
